import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { RideSchema } from '../../../../types/simulation';

const TARMAC = '#3A3A48';
const TEAL = '#7DC8C6';
const FONT_FAMILY = '"Roboto", sans-serif';

const DURATION_BUCKETS = [
  { label: '0-10m', min: 0, max: 10 },
  { label: '10-20m', min: 10, max: 20 },
  { label: '20-30m', min: 20, max: 30 },
  { label: '30-40m', min: 30, max: 40 },
  { label: '40-50m', min: 40, max: 50 },
  { label: '50-60m', min: 50, max: 60 },
  { label: '60m+', min: 60, max: Infinity },
];

export interface TravelTimeChartProps {
  data: RideSchema[] | null | undefined;
}

const TravelTimeChart: React.FC<TravelTimeChartProps> = ({ data }) => {
  const { t } = useTranslation('simulation');
  const { buckets, maxCount, hasDuration } = useMemo(() => {
    const points = data ?? [];
    const withDuration = points.filter(
      (p) => typeof (p as RideSchema & { duration_minutes?: number }).duration_minutes === 'number'
    );
    const counts = DURATION_BUCKETS.map((b) => ({
      ...b,
      count: withDuration.filter((p) => {
        const d = (p as RideSchema & { duration_minutes?: number }).duration_minutes!;
        return d >= b.min && d < b.max;
      }).length,
    }));
    const maxCount = Math.max(1, ...counts.map((c) => c.count));
    return {
      buckets: counts,
      maxCount,
      hasDuration: withDuration.length > 0,
    };
  }, [data]);

  return (
    <Box
      sx={{
        backgroundColor: TARMAC,
        fontFamily: FONT_FAMILY,
        height: '100%',
        minHeight: 140,
        display: 'flex',
        flexDirection: 'column',
        px: 2,
        py: 1.5,
      }}
    >
<Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#171a1f',
            mb: 1,
          }}
        >
          {t('results.charts.travelTimeTitle')}
        </Typography>
      {!hasDuration ? (
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: '0.8rem',
            color: '#6b7280',
          }}
        >
          {t('results.charts.travelTimeNoData')}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 0.5,
            flex: 1,
            minHeight: 80,
          }}
        >
          {buckets.map((b) => {
            const heightPct = maxCount > 0 ? (b.count / maxCount) * 100 : 0;
            return (
              <Box
                key={b.label}
                sx={{
                  flex: 1,
                  minWidth: 24,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: Math.max(4, heightPct) + '%',
                    minHeight: 8,
                    backgroundColor: TEAL,
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.2s ease',
                  }}
                  title={t('results.charts.bucketTripsLabel', { label: b.label, value: b.count })}
                />
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontSize: '0.65rem',
                    color: '#374151',
                    mt: 0.25,
                  }}
                >
                  {b.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: '#fff',
                  }}
                >
                  {b.count}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default TravelTimeChart;
